import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

interface SectionInfoCardProps {
  title: string;
  message?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;

  /**
   * Número de columnas en las que se distribuirá el contenido
   * 1 = 1 columna
   * 2 = 2 columnas (default recomendado)
   * 3 = 3 columnas
   * 4 = 4 columnas
   */
  columns?: 1 | 2 | 3 | 4;

  /**
   * Tamaño de separación entre elementos (Tailwind gap-N)
   * ej: 2, 4, 6
   */
  gap?: number;
}

const SectionInfoCard: React.FC<SectionInfoCardProps> = ({
  title,
  message,
  children,
  defaultOpen = false,
  columns = 1,
  gap = 4,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  // Construye clases dinámicas basadas en columnas
  const getGridClass = () => {
    switch (columns) {
      case 2:
        return "grid grid-cols-1 sm:grid-cols-2";
      case 3:
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
      case 4:
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";
      default:
        return "flex flex-col"; // 1 columna simple
    }
  };

  return (
    <div
      className="
        rounded-md border border-stroke bg-white shadow-default
        dark:border-strokedark dark:bg-boxdark
        transition-all duration-300
        mt-4
      "
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="
          w-full flex items-center justify-between gap-2
          dark:bg-black/50
          px-3 py-3 sm:px-4 sm:py-4
          hover:bg-gray-100 dark:hover:bg-gray-800
          cursor-pointer transition
        "
      >
        <div className="flex flex-col items-start">
          <h2 className="text-sm sm:text-base font-semibold text-black dark:text-white">
            {title}
          </h2>

          {message && (
            <span
              className="
                text-xs text-gray-500 dark:text-gray-400 mt-0.5
                leading-tight
              "
            >
              {message}
            </span>
          )}
        </div>

        <div className="flex-shrink-0">
          {open ? (
            <ChevronUp className="text-primary" size={18} />
          ) : (
            <ChevronDown className="text-gray-500" size={18} />
          )}
        </div>
      </button>

      {/* Content */}
      {open && (
        <div
          className="
            border-t border-stroke dark:border-strokedark dark:bg-boxdark-2/25
            px-3 py-3 sm:px-4 sm:py-4
            text-sm
          "
        >
          <div className={`${getGridClass()} gap-${gap}`}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionInfoCard;
